import React from 'react'

// Components
import RowGroup from '../RowGroup'
import Button, { LinkButton } from 'components/Button'

// Constants
import { routes } from 'constants/routes'

// Utils
import { formatPriceShort } from 'utils/money'
import { PLAN_TYPE_LABEL } from 'modules/workspace/utils'
import dateFormat from 'utils/localizedDateFormat'

// GQL
import { BillingPlanFragment } from 'gqlTypes'

// Styles
import { Row, ContentCell } from '../PlanOverviewPanel/PlanOverviewPanel.styles'

interface CurrentPlanProps {
  workspaceId: string
  isWorkspaceOnTrial: boolean
  customerHasPaymentDetails: boolean
  currentPlan?: BillingPlanFragment
  trialEndDate: string
  isSubscriptionEnd: boolean
  onReactivateSubscription: () => void
}

const Header: React.FC = () => (
  <RowGroup.HeadingCell width={['70%', 1 / 4]}>
    Current Plan
  </RowGroup.HeadingCell>
)

export const CurrentPlan = ({
  workspaceId,
  isWorkspaceOnTrial,
  customerHasPaymentDetails,
  currentPlan,
  trialEndDate,
  isSubscriptionEnd,
  onReactivateSubscription,
}: CurrentPlanProps) => {
  /**
   * The workspace is on trial and the customer already configured a following
   * plan
   */
  if (isWorkspaceOnTrial && currentPlan && customerHasPaymentDetails) {
    return (
      <Row>
        <Header />

        <ContentCell width={['70%', 1 / 4]}>
          <div>
            <b>Trial</b>
            <br />
            Ends on {dateFormat(new Date(trialEndDate))}
          </div>
        </ContentCell>

        {isSubscriptionEnd && (
          <ContentCell justifyContent="flex-end" alignItems="center">
            <Button variant="primary" tiny onClick={onReactivateSubscription}>
              Subscribe Again
            </Button>
          </ContentCell>
        )}
      </Row>
    )
  } else if (isWorkspaceOnTrial) {
    /**
     * The workspace is only on trial. After that, the workspace will be
     * de-activated
     */
    return (
      <Row>
        <Header />
        <ContentCell width={['70%', 1 / 4]}>
          <div>
            <b>Trial</b>
            <br />
            Ends on {dateFormat(new Date(trialEndDate))}
          </div>
        </ContentCell>
        <ContentCell justifyContent="flex-end" alignItems="center">
          <LinkButton
            data-testId="setup-subscription-button"
            variant="primary"
            tiny
            to={routes.WORKSPACE_SUBSCRIBE.create({
              workspaceId,
            })}>
            Set Up a Subscription
          </LinkButton>
        </ContentCell>
      </Row>
    )
  } else {
    /**
     * The workspace is definitely not on trial
     */
    return (
      <Row>
        <Header />

        <ContentCell width={['70%', 1 / 4]}>
          <div>
            <b data-testid="plan-name">{PLAN_TYPE_LABEL[currentPlan!.type]}</b>
            <br />
            <span data-testid="plan-price-per-editor">
              <b>{formatPriceShort(currentPlan!.invoicePrice)}</b> per Seat, per{' '}
              {currentPlan?.type === 'YEARLY' ? 'year' : 'month'}
            </span>
          </div>
        </ContentCell>
        {isSubscriptionEnd && (
          <ContentCell justifyContent="flex-end" alignItems="center">
            <Button variant="primary" tiny onClick={onReactivateSubscription}>
              Subscribe Again
            </Button>
          </ContentCell>
        )}
      </Row>
    )
  }
}
